import React, { useCallback, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { jwtDecode } from 'jwt-decode';
import { Bell, X } from 'lucide-react';
import { API_BASE_URL } from '../config/config';
import OpsLayout from '../components/OpsLayout';
import opsStatusTone from '../utils/opsStatusTone';
import '../css/PushAnnouncements.css';

// 공지 푸시. 최고관리자가 앱 사용자에게 제목·내용·이동할 페이지를 정해 푸시로 보낸다.
// 서버: src/server/announcements (설계·운영 docs/push-announcements.md).
//
//   · 받는 사람: 전체, 또는 사용자 그룹 여럿(겹치는 사람은 한 번만)
//   · 이동할 페이지: 공지 내용 보기 / 프레일 평가 / 웹 페이지 — 지금 앱이 처리하는 것만. 다른 화면은 앱 배포가 필요하다.
//   · 시험 발송: 휴대폰 번호 한 명에게. 드라이런 모드여도 실제로 나간다.
//   · 보낸 공지는 앱 알림함에 '공지' 로 남는다. 푸시를 받을 기기가 없는 사람도 알림함에서는 본다.
//
// ⚠️ 실발송/드라이런은 서버 플래그(ANNOUNCEMENT_FCM_DRY_RUN)가 정한다. 이 화면은 보여 줄 뿐 바꾸지 못한다.

const TITLE = '공지 푸시';

const readRole = () => {
    try {
        const decoded = jwtDecode(localStorage.getItem('token'));
        return decoded?.role || decoded?.user_role || null;
    } catch (error) { return null; }
};

const authHeaders = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } });

const describeError = (error, fallback) => {
    const status = error?.response?.status;
    const message = error?.response?.data?.message;
    if (message) return message;
    if (status === 401) return '세션이 만료되었습니다. 다시 로그인해 주세요.';
    if (status === 403) return '권한이 없습니다. 최고관리자 계정으로 로그인해 주세요.';
    if (error && !error.response) return '서버에 연결하지 못했습니다.';
    return fallback;
};

const fmtTime = (iso) => {
    if (!iso) return '-';
    const d = new Date(iso);
    return `${d.getMonth() + 1}/${d.getDate()} ${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
};

const STATUS_LABEL = { sending: '진행중', done: '완료', failed: '실패' };
const DESTINATION_HINT = {
    notice: '알림함이 열리고, 공지를 누르면 전문이 보입니다.',
    frail_assessment: '프레일 평가 안내 화면이 열립니다. 거기서 [프레일 평가 하기]로 설문을 시작합니다.',
    web: '입력한 주소를 앱 안에서 엽니다.'
};

const EMPTY_FORM = { title: '', body: '', destination: 'notice', url: '' };

export default function PushAnnouncements() {
    const [role] = useState(readRole);
    const isSuperAdmin = role === 'SUPER_ADMIN';

    const [config, setConfig] = useState(null);
    const [all, setAll] = useState(null);
    const [groups, setGroups] = useState([]);
    const [history, setHistory] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [notice, setNotice] = useState(null);

    const [form, setForm] = useState(EMPTY_FORM);
    const [targetType, setTargetType] = useState('all');
    const [groupIds, setGroupIds] = useState([]);
    const [groupAudience, setGroupAudience] = useState(null);
    const [testMobile, setTestMobile] = useState('');
    const [busy, setBusy] = useState(null);           // 'test' | 'send'
    const [confirming, setConfirming] = useState(false);

    const load = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            const [cf, gr, hi] = await Promise.all([
                axios.get(`${API_BASE_URL}/api/push-announcements/config`, authHeaders()),
                axios.get(`${API_BASE_URL}/api/push-announcements/groups`, authHeaders()),
                axios.get(`${API_BASE_URL}/api/push-announcements`, { ...authHeaders(), params: { limit: 30 } })
            ]);
            setConfig(cf.data);
            setAll(gr.data.all);
            setGroups(gr.data.groups || []);
            setHistory(hi.data.history || []);
        } catch (e) {
            setError(describeError(e, '화면을 불러오지 못했습니다.'));
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => { if (isSuperAdmin) load(); }, [isSuperAdmin, load]);

    const reloadHistory = useCallback(async () => {
        try {
            const hi = await axios.get(`${API_BASE_URL}/api/push-announcements`, { ...authHeaders(), params: { limit: 30 } });
            setHistory(hi.data.history || []);
        } catch (e) { /* 이력 새로 고침 실패는 조용히 넘긴다 — 다음 새로 고침에서 다시 본다 */ }
    }, []);

    // 진행 중인 발송이 있으면 3초마다 이력을 다시 읽는다.
    const hasRunning = history.some((h) => h.status === 'sending');
    useEffect(() => {
        if (!hasRunning) return undefined;
        const timer = setInterval(reloadHistory, 3000);
        return () => clearInterval(timer);
    }, [hasRunning, reloadHistory]);

    // 그룹을 고르면 겹치는 사람을 빼고 센 인원을 서버에서 받는다.
    useEffect(() => {
        if (targetType !== 'groups' || groupIds.length === 0) { setGroupAudience(null); return undefined; }
        let cancelled = false;
        axios.post(`${API_BASE_URL}/api/push-announcements/audience`, { groupIds }, authHeaders())
            .then((r) => { if (!cancelled) setGroupAudience(r.data); })
            .catch(() => { if (!cancelled) setGroupAudience(null); });
        return () => { cancelled = true; };
    }, [targetType, groupIds]);

    const audience = targetType === 'all' ? all : groupAudience;
    const limits = config?.limits || { title: 60, body: 1000 };
    const destinations = config?.destinations || [];

    const formError = useMemo(() => {
        if (!form.title.trim()) return '제목을 입력해 주세요.';
        if (!form.body.trim()) return '내용을 입력해 주세요.';
        if (form.destination === 'web' && !/^https?:\/\/\S+$/i.test(form.url.trim())) return '웹 페이지 주소는 http:// 또는 https:// 로 시작해야 합니다.';
        return null;
    }, [form]);
    const targetError = targetType === 'groups' && groupIds.length === 0 ? '사용자 그룹을 하나 이상 골라 주세요.' : null;

    const setField = (key) => (e) => setForm((prev) => ({ ...prev, [key]: e.target.value }));
    const toggleGroup = (id) => setGroupIds((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));
    const contentBody = () => ({ title: form.title, body: form.body, destination: form.destination, url: form.destination === 'web' ? form.url : '' });

    const sendTest = async () => {
        setBusy('test');
        setError(null);
        try {
            const r = await axios.post(`${API_BASE_URL}/api/push-announcements/test`, { ...contentBody(), mobile: testMobile }, authHeaders());
            setNotice(r.data.sent > 0 ? '시험 푸시를 보냈습니다. 휴대폰에서 확인해 주세요.' : '시험 푸시가 나가지 않았습니다. 아래 이력의 실패 사유를 확인해 주세요.');
            await reloadHistory();
        } catch (e) { setError(describeError(e, '시험 발송에 실패했습니다.')); }
        finally { setBusy(null); }
    };

    const send = async () => {
        setConfirming(false);
        setBusy('send');
        setError(null);
        try {
            const r = await axios.post(`${API_BASE_URL}/api/push-announcements`,
                { ...contentBody(), targetType, groupIds: targetType === 'groups' ? groupIds : [] }, authHeaders());
            setNotice(`공지 #${r.data.id} 발송을 시작했습니다 — ${r.data.total}명${r.data.dryRun ? ' (드라이런: 실제로 보내지 않음)' : ''}.`);
            setForm(EMPTY_FORM);
            await reloadHistory();
        } catch (e) { setError(describeError(e, '공지 발송에 실패했습니다.')); }
        finally { setBusy(null); }
    };

    if (!isSuperAdmin) {
        return (
            <OpsLayout title={TITLE}>
                <section className="ops-page__card"><div className="sds-notice">최고관리자만 사용할 수 있는 화면입니다.</div></section>
            </OpsLayout>
        );
    }

    const modeLabel = config ? (config.dryRun ? '드라이런 모드' : '실발송 모드') : null;
    const modeTone = config?.dryRun ? 'caution' : 'info';
    const targetLabel = targetType === 'all'
        ? '전체 사용자'
        : groups.filter((g) => groupIds.includes(g.id)).map((g) => g.name).join(', ');

    return (
        <OpsLayout
            title={TITLE}
            description="앱 사용자에게 공지를 푸시로 보냅니다. 보낸 공지는 앱 알림함에도 남습니다."
            actions={modeLabel && (
                <span className={`sds-badge sds-badge--${modeTone}`}
                    title="서버 환경변수(ANNOUNCEMENT_FCM_DRY_RUN)가 정합니다. 화면에서 바꿀 수 없습니다.">
                    {modeLabel}
                </span>
            )}
        >
            <section className="ops-page__card announce-page">
                {notice && (
                    <div className="sds-notice">
                        {notice}
                        <span className="sds-toolbar__spacer" />
                        <button className="sds-btn sds-btn--ghost sds-btn--sm" onClick={() => setNotice(null)} aria-label="닫기"><X size={16} aria-hidden="true" /></button>
                    </div>
                )}
                {error && (
                    <div className="sds-notice sds-notice--error">
                        {error}
                        <span className="sds-toolbar__spacer" />
                        <button className="sds-btn sds-btn--ghost sds-btn--sm" onClick={() => setError(null)} aria-label="닫기"><X size={16} aria-hidden="true" /></button>
                    </div>
                )}
                {config?.dryRun && (
                    <div className="sds-notice sds-notice--info">
                        지금은 드라이런 모드라 [발송]을 눌러도 사용자에게 나가지 않고 기록만 남습니다. [시험 발송]은 실제로 나갑니다.
                    </div>
                )}

                {loading ? <div className="sds-notice">불러오는 중…</div> : (
                    <div className="announce-page__compose">
                        <div className="announce-page__form">
                            <div className="sds-field">
                                <label className="sds-label" htmlFor="announce-title">제목</label>
                                <input id="announce-title" className="sds-input" maxLength={limits.title} value={form.title} onChange={setField('title')} placeholder="예) 11월 5일 새벽 서비스 점검 안내" />
                                <span className="announce-page__count">{form.title.length} / {limits.title}</span>
                            </div>
                            <div className="sds-field">
                                <label className="sds-label" htmlFor="announce-body">내용</label>
                                <textarea id="announce-body" className="sds-textarea announce-page__body" maxLength={limits.body} value={form.body} onChange={setField('body')} />
                                <span className="announce-page__count">{form.body.length} / {limits.body}</span>
                            </div>
                            <div className="sds-field">
                                <span className="sds-label">이동할 페이지</span>
                                <div className="sds-seg" role="radiogroup" aria-label="이동할 페이지">
                                    {destinations.map((d) => {
                                        const active = form.destination === d.code;
                                        return (
                                            <button key={d.code} type="button" role="radio" aria-checked={active}
                                                className={`sds-seg__item${active ? ' is-active' : ''}`}
                                                onClick={() => setForm((prev) => ({ ...prev, destination: d.code }))}>
                                                {d.label}
                                            </button>
                                        );
                                    })}
                                </div>
                                <span className="announce-page__hint">{DESTINATION_HINT[form.destination]}</span>
                                {form.destination === 'web' && (
                                    <input className="sds-input" value={form.url} onChange={setField('url')} placeholder="https://" aria-label="웹 페이지 주소" />
                                )}
                            </div>

                            <div className="sds-field">
                                <span className="sds-label">받는 사람</span>
                                <div className="sds-seg" role="radiogroup" aria-label="받는 사람">
                                    {[['all', '전체 사용자'], ['groups', '사용자 그룹 선택']].map(([value, label]) => {
                                        const active = targetType === value;
                                        return (
                                            <button key={value} type="button" role="radio" aria-checked={active}
                                                className={`sds-seg__item${active ? ' is-active' : ''}`} onClick={() => setTargetType(value)}>
                                                {label}
                                            </button>
                                        );
                                    })}
                                </div>
                                {targetType === 'groups' && (
                                    <div className="announce-page__groups" role="group" aria-label="사용자 그룹">
                                        {groups.map((g) => (
                                            <label key={g.id} className="announce-page__group">
                                                <input type="checkbox" checked={groupIds.includes(g.id)} onChange={() => toggleGroup(g.id)} />
                                                <span className="announce-page__group-name">{g.name}</span>
                                                <span className="announce-page__group-count">{g.memberCount}명 · 푸시 {g.pushCount}</span>
                                            </label>
                                        ))}
                                    </div>
                                )}
                                {audience && (
                                    <p className="announce-page__audience">
                                        <strong>{audience.total}명</strong>에게 보냅니다 — 푸시 {audience.push}명, 기기가 없어 알림함에만 {audience.inAppOnly}명
                                    </p>
                                )}
                            </div>

                            <div className="announce-page__test">
                                <div className="sds-field announce-page__test-field">
                                    <label className="sds-label" htmlFor="announce-test">시험 발송 받을 휴대폰 번호</label>
                                    <input id="announce-test" className="sds-input" value={testMobile} onChange={(e) => setTestMobile(e.target.value)} placeholder="01012345678" inputMode="numeric" />
                                </div>
                                <button className="sds-btn sds-btn--secondary" disabled={!!busy || !!formError || testMobile.replace(/\D/g, '').length < 10} onClick={sendTest}>
                                    {busy === 'test' ? '보내는 중…' : '시험 발송'}
                                </button>
                            </div>

                            <div className="announce-page__actions">
                                {/* 빈 화면에서부터 빨간 글씨를 띄우지 않는다 — 무언가 쓰기 시작한 뒤에만 알려 준다. */}
                                {(form.title || form.body) && (formError || targetError) && <span className="sds-field-error">{formError || targetError}</span>}
                                <span className="sds-toolbar__spacer" />
                                <button className="sds-btn sds-btn--primary" disabled={!!busy || !!formError || !!targetError || !audience || hasRunning}
                                    onClick={() => setConfirming(true)}>
                                    {busy === 'send' ? '보내는 중…' : hasRunning ? '다른 공지 발송 중' : '발송'}
                                </button>
                            </div>
                        </div>

                        <aside className="announce-page__preview" aria-label="미리보기">
                            <span className="sds-label">미리보기</span>
                            <div className="announce-page__push">
                                <div className="announce-page__push-head"><Bell size={14} aria-hidden="true" /> V-Care · 지금</div>
                                <div className="announce-page__push-title">{form.title || '제목'}</div>
                                <div className="announce-page__push-body">{form.body || '내용이 여기에 보입니다.'}</div>
                            </div>
                        </aside>
                    </div>
                )}
            </section>

            <section className="ops-page__card announce-page">
                <h2 className="sds-section-title">발송 이력</h2>
                {history.length === 0 ? <div className="sds-empty">아직 보낸 공지가 없습니다.</div> : (
                    <div className="sds-table-wrap">
                        <table className="sds-table">
                            <thead>
                                <tr>
                                    <th>보낸 시각</th>
                                    <th>제목</th>
                                    <th>이동</th>
                                    <th>받는 사람</th>
                                    <th className="sds-table__center">대상</th>
                                    <th className="sds-table__center">푸시</th>
                                    <th className="sds-table__center">실패</th>
                                    <th className="sds-table__center">알림함만</th>
                                    <th>상태</th>
                                    <th>보낸 사람</th>
                                </tr>
                            </thead>
                            <tbody>
                                {history.map((h) => {
                                    const statusLabel = STATUS_LABEL[h.status] || h.status;
                                    const dest = destinations.find((d) => d.code === h.destination)?.label || h.destination;
                                    const who = h.targetType === 'test' ? '시험 발송' : h.targetType === 'all' ? '전체' : h.groupNames.join(', ');
                                    return (
                                        <tr key={h.id}>
                                            <td className="sds-table__muted">{fmtTime(h.createdAt)}</td>
                                            <td className="announce-page__history-title" title={h.body}>{h.title}</td>
                                            <td>{dest}</td>
                                            <td>{who}</td>
                                            <td className="sds-table__center">{h.targetCount}</td>
                                            <td className="sds-table__center">{h.sentCount}</td>
                                            <td className={`sds-table__center${h.failedCount > 0 ? ' announce-page__num--danger' : ''}`}>{h.failedCount}</td>
                                            <td className="sds-table__center">{h.inAppCount}</td>
                                            <td>
                                                <span className={`sds-badge sds-badge--${opsStatusTone(statusLabel)}`} title={h.error || ''}>
                                                    {statusLabel}{h.dryRun ? ' (드라이런)' : ''}
                                                </span>
                                            </td>
                                            <td className="sds-table__muted">{h.createdBy || '-'}</td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>
                )}
            </section>

            {confirming && (
                <div className="sds-modal-overlay" onClick={() => setConfirming(false)}>
                    <div className="sds-modal" role="dialog" aria-modal="true" aria-labelledby="announce-confirm-title" onClick={(e) => e.stopPropagation()}>
                        <div className="sds-modal__head">
                            <h2 id="announce-confirm-title" className="sds-modal__title">공지를 보낼까요?</h2>
                            <button className="sds-btn sds-btn--ghost sds-btn--sm sds-btn--icon" onClick={() => setConfirming(false)} aria-label="닫기"><X size={16} aria-hidden="true" /></button>
                        </div>
                        <div className="sds-modal__body">
                            <dl className="announce-page__confirm">
                                <div><dt>받는 사람</dt><dd>{targetLabel} · {audience?.total}명 (푸시 {audience?.push}명)</dd></div>
                                <div><dt>제목</dt><dd>{form.title}</dd></div>
                                <div><dt>이동할 페이지</dt><dd>{destinations.find((d) => d.code === form.destination)?.label}{form.destination === 'web' ? ` — ${form.url}` : ''}</dd></div>
                            </dl>
                            <p className="announce-page__hint">
                                {config?.dryRun ? '드라이런 모드라 실제로 나가지 않고 기록만 남습니다.' : '보낸 푸시는 되돌릴 수 없습니다.'}
                            </p>
                        </div>
                        <div className="sds-modal__foot">
                            <button className="sds-btn sds-btn--ghost" onClick={() => setConfirming(false)}>취소</button>
                            <button className="sds-btn sds-btn--primary" onClick={send}>{audience?.total}명에게 보내기</button>
                        </div>
                    </div>
                </div>
            )}
        </OpsLayout>
    );
}
